// src/features/feedback/components/VoteButton.jsx
import React from 'react';
import {
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {
  ThumbUp,
  ThumbUpOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeedbackVote } from '../hooks/useFeedbackVote';

export default function VoteButton({ 
  feedbackId, 
  userHasVoted, 
  totalVotes, 
  onVoteChange,
  variant = 'button', // 'button' or 'icon'
  size = 'medium',
  disabled = false,
}) {
  const { t } = useTranslation(['feedback', 'common']);
  const { voting, toggleVote } = useFeedbackVote();

  const handleVoteClick = async (event) => {
    event.stopPropagation(); // Prevent row click in tables
    
    const result = await toggleVote(feedbackId, userHasVoted);
    
    if (result.success && onVoteChange) {
      onVoteChange(result.data);
    }
  };

  const isDisabled = disabled || voting;

  if (variant === 'icon') {
    return (
      <Tooltip 
        title={
          userHasVoted 
            ? t('feedback:vote.remove_vote', 'Remove your vote') 
            : t('feedback:vote.add_vote', 'Vote for this feedback')
        }
      >
        <span>
          <IconButton
            onClick={handleVoteClick}
            disabled={isDisabled}
            color={userHasVoted ? 'primary' : 'default'}
            size={size}
          >
            {voting ? (
              <CircularProgress size={20} />
            ) : userHasVoted ? (
              <ThumbUp />
            ) : (
              <ThumbUpOutlined />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button
      variant={userHasVoted ? 'contained' : 'outlined'}
      color={userHasVoted ? 'primary' : 'inherit'}
      startIcon={
        voting ? (
          <CircularProgress size={16} />
        ) : userHasVoted ? (
          <ThumbUp />
        ) : (
          <ThumbUpOutlined />
        )
      }
      onClick={handleVoteClick}
      disabled={isDisabled}
      size={size}
      sx={{
        minWidth: 100,
        fontWeight: 600,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight="inherit">
          {totalVotes}
        </Typography>
        <Typography variant="body2" fontWeight="inherit">
          {totalVotes === 1 
            ? t('feedback:vote.vote_singular', 'Vote')
            : t('feedback:vote.vote_plural', 'Votes')
          }
        </Typography>
      </Box>
    </Button>
  );
}